/**
 * @copyright Copyright 2023-2023 Epic Systems Corporation
 * @file showroom wrapper for NotImplementingNowButton
 * @module Epic.AppOrchard.Showroom.NotImplementingNowButtonWrapper
 */

import { Button, ButtonProps, useToast } from "@chakra-ui/react";
import { ISuccessHandler } from "@epic/react-async-hook";
import { config } from "ao/appConfig";
import { markAsNotImplementingNow } from "ao/components/Dashboard/data";
import { MarkNowImplementingNowButton } from "ao/components/Dashboard/widgets/nominatedApps/helperComponents/MarkNotImplementingNowButton";
import { INominatedApp, IUserSecurity } from "ao/types";
import { IShowroomAppDownloadModel } from "ao/types/showroom";
import React, { FunctionComponent, memo, useCallback } from "react";

interface IProps {
	appDetails: IShowroomAppDownloadModel;
	userSecurity: IUserSecurity;
	setAppDetails: (x: IShowroomAppDownloadModel) => void;
	buttonProps?: ButtonProps;
	iconColor?: string;
}

export const NotImplementingNowButtonWrapper: FunctionComponent<IProps> = memo((props) => {
	const { appDetails, userSecurity, setAppDetails, buttonProps, iconColor } = props;

	const notImplementingNowDetails = appDetails.notImplementingNowDetails;

	//INominated app Partial object
	const nominatedApp: Partial<INominatedApp> = {
		appId: appDetails.appId,
		nominations: [],
		notNowDecision: appDetails.notImplementingNowDetails,
	};

	const toast = useToast();

	const handleSuccess = useCallback<ISuccessHandler<typeof markAsNotImplementingNow>>(
		(response) => {
			setAppDetails({
				...appDetails,
				notImplementingNowDetails: response!,
			});
			toast({
				title: "Success",
				description: "Saved changes",
				status: "success",
				duration: config.ToastDuration,
				isClosable: true,
			});
		},
		[appDetails, setAppDetails, toast],
	);

	const handleFailure = useCallback(() => {
		toast({
			title: "Error",
			description: "Error saving the last change to the server.",
			status: "error",
			duration: config.ToastDuration,
			isClosable: true,
		});
	}, [toast]);

	return (
		<MarkNowImplementingNowButton
			app={nominatedApp}
			orgId={userSecurity.userOrganizationId!}
			successHandler={handleSuccess}
			failureHandler={handleFailure}
			interestedCount={appDetails.interestedCount}
			buttonAs={Button}
			{...buttonProps}
			buttonText={notImplementingNowDetails ? "Edit Status" : "Not Implementing Now"}
			excludeIcon
			iconColor={iconColor}
		/>
	);
});
